import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import PageTitle from "../../layouts/PageTitle";
import {
  bulkBlogActionApi,
  deleteBlogApi,
  getBlogCategoriesApi,
  getBlogsApi,
} from "../../../services/BlogService";

const formatDate = (value) => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }
  return date.toLocaleString();
};

const AllBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const [filters, setFilters] = useState({
    search: "",
    category: "",
    status: "",
    author: "",
    dateFrom: "",
    dateTo: "",
  });

  const areAllSelected = useMemo(() => {
    if (!blogs.length) {
      return false;
    }
    return blogs.every((blog) => selectedIds.includes(blog.id));
  }, [blogs, selectedIds]);

  const loadData = async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const [blogResponse, categoryResponse] = await Promise.all([
        getBlogsApi(filters),
        getBlogCategoriesApi(),
      ]);
      setBlogs(
        Array.isArray(blogResponse?.data?.data) ? blogResponse.data.data : [],
      );
      setCategories(
        Array.isArray(categoryResponse?.data?.data)
          ? categoryResponse.data.data
          : [],
      );
    } catch (error) {
      setErrorMessage("Unable to load blogs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const applyFilters = async () => {
    await loadData();
  };

  const resetFilters = async () => {
    setFilters({
      search: "",
      category: "",
      status: "",
      author: "",
      dateFrom: "",
      dateTo: "",
    });
    setSelectedIds([]);
    try {
      setLoading(true);
      const response = await getBlogsApi();
      setBlogs(Array.isArray(response?.data?.data) ? response.data.data : []);
    } catch (error) {
      setErrorMessage("Unable to reset filters.");
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (blogId) => {
    setSelectedIds((previous) =>
      previous.includes(blogId)
        ? previous.filter((item) => item !== blogId)
        : [...previous, blogId],
    );
  };

  const toggleSelectAll = () => {
    if (areAllSelected) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(blogs.map((item) => item.id));
  };

  const handleBulkAction = async (action) => {
    if (!selectedIds.length) {
      setErrorMessage("Select at least one blog for bulk action.");
      return;
    }

    try {
      setErrorMessage("");
      setSuccessMessage("");
      await bulkBlogActionApi(action, selectedIds);
      setSuccessMessage(`Bulk ${action} completed successfully.`);
      setSelectedIds([]);
      await loadData();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || "Bulk action failed.");
    }
  };

  const handleSingleDelete = async (blogId) => {
    try {
      setErrorMessage("");
      setSuccessMessage("");
      await deleteBlogApi(blogId);
      setSuccessMessage("Blog deleted successfully.");
      await loadData();
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || "Unable to delete blog.",
      );
    }
  };

  return (
    <>
      <PageTitle activeMenu={"All Blogs"} motherMenu={"Blogs"} />

      <div className="card mb-4">
        <div className="card-header">
          <h4 className="card-title mb-0">Search & Filters</h4>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <input
                type="text"
                className="form-control"
                placeholder="Search by title, excerpt, content"
                value={filters.search}
                onChange={(event) =>
                  setFilters((previous) => ({
                    ...previous,
                    search: event.target.value,
                  }))
                }
              />
            </div>
            <div className="col-md-2">
              <select
                className="form-control"
                value={filters.category}
                onChange={(event) =>
                  setFilters((previous) => ({
                    ...previous,
                    category: event.target.value,
                  }))
                }
              >
                <option value="">All Categories</option>
                {categories.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-control"
                value={filters.status}
                onChange={(event) =>
                  setFilters((previous) => ({
                    ...previous,
                    status: event.target.value,
                  }))
                }
              >
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="scheduled">Scheduled</option>
              </select>
            </div>
            <div className="col-md-2">
              <input
                type="text"
                className="form-control"
                placeholder="Author"
                value={filters.author}
                onChange={(event) =>
                  setFilters((previous) => ({
                    ...previous,
                    author: event.target.value,
                  }))
                }
              />
            </div>
            <div className="col-md-1">
              <input
                type="date"
                className="form-control"
                value={filters.dateFrom}
                onChange={(event) =>
                  setFilters((previous) => ({
                    ...previous,
                    dateFrom: event.target.value,
                  }))
                }
              />
            </div>
            <div className="col-md-1">
              <input
                type="date"
                className="form-control"
                value={filters.dateTo}
                onChange={(event) =>
                  setFilters((previous) => ({
                    ...previous,
                    dateTo: event.target.value,
                  }))
                }
              />
            </div>
          </div>

          <div className="mt-3 d-flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={applyFilters}
            >
              Apply
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={resetFilters}
            >
              Reset
            </button>
            <Link to="/add-blog" className="btn btn-success btn-sm">
              Add Blog
            </Link>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title mb-0">Blog Table</h4>
          {/* <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => handleBulkAction("publish")}
            >
              Bulk Publish
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-warning"
              onClick={() => handleBulkAction("unpublish")}
            >
              Bulk Unpublish
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleBulkAction("delete")}
            >
              Bulk Delete
            </button>
          </div> */}
        </div>
        <div className="card-body">
          {errorMessage ? (
            <div className="alert alert-danger">{errorMessage}</div>
          ) : null}
          {successMessage ? (
            <div className="alert alert-success">{successMessage}</div>
          ) : null}

          <div className="table-responsive">
            <table className="table table-bordered table-striped align-middle">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={areAllSelected}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Author</th>
                  <th>Language</th>
                  <th>Publish Date</th>
                  <th>Reading Time</th>
                  <th>Featured</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" className="text-center py-4">
                      Loading blogs...
                    </td>
                  </tr>
                ) : null}

                {!loading && !blogs.length ? (
                  <tr>
                    <td colSpan="10" className="text-center py-4">
                      No blogs found.
                    </td>
                  </tr>
                ) : null}

                {!loading
                  ? blogs.map((blog) => (
                      <tr key={blog.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(blog.id)}
                            onChange={() => toggleSelection(blog.id)}
                          />
                        </td>
                        <td>
                          <strong>{blog.title}</strong>
                          <div className="text-muted small">/{blog.slug}</div>
                        </td>
                        <td>{blog.category || "-"}</td>
                        <td>
                          <span className="badge badge-info light">
                            {blog.publishStatus || "draft"}
                          </span>
                        </td>
                        <td>{blog.authorName || "-"}</td>
                        <td>{blog.language || "en"}</td>
                        <td>
                          {formatDate(blog.publishDate || blog.createdAt)}
                        </td>
                        <td>{blog.readingTime || 1} min</td>
                        <td>{blog.isFeatured ? "Yes" : "No"}</td>

                        <td
                          style={{
                            flexDirection: "row",
                            justifyContent: "center",
                            alignItems: "center",
                            display: "flex",
                            backgroundColor: "#fff",
                          }}
                        >
                          <Link
                            className="btn btn-sm btn-outline-info me-1"
                            to={`/blog-detail/${blog.id}`}
                          >
                            View
                          </Link>
                          <Link
                            className="btn btn-sm btn-outline-secondary me-2"
                            to={`/edit-blog/${blog.id}`}
                          >
                            Edit
                          </Link>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleSingleDelete(blog.id)}
                          >
                            Delete
                          </button>
                          <Link
                            style={{ marginLeft: "10px" }}
                            className="btn btn-sm btn-outline-secondary me-2"
                            to={`/blog-detail/${blog.id}`}
                          >
                            View
                          </Link>
                        </td>
                      </tr>
                    ))
                  : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default AllBlogs;
